import * as uuid from "uuid"
import * as httpx from "httpx"
import * as layouts from "layouts"
import * as inputs from "inputs"
import { CancellablePromise } from "real-cancellable-promise"
import * as api from "brandguard/api"
import { UnifiedItem, assetTypeForFile, unifiedItemDisplay } from "./types"
import { fanoutSearch, uploadsSearchers } from "./fanout"

// uploadFile posts a file to the endpoint for its detected asset type and tags
// the response so it joins the unified stream like a searched item would.
export function uploadFile(file: File, bid: string, bearer: httpx.option): CancellablePromise<UnifiedItem> {
	switch (assetTypeForFile(file)) {
		case "image":
			return api.images.uploads.create(file, bid, bearer).then((data) => ({ event_type: "image" as const, data }))
		case "text":
			return api.text.uploads.create(file, bid, bearer).then((data) => ({ event_type: "text" as const, data }))
		case "document":
			return api.documents.uploads.create(file, bid, bearer).then((data) => ({ event_type: "document" as const, data }))
		case "video":
			return api.videos.uploads.create(file, bid, bearer).then((data) => ({ event_type: "video" as const, data }))
		default:
			return CancellablePromise.reject(new Error(`unsupported file: ${file.name}`))
	}
}

// refreshUpload re-reads an in-flight upload from its own endpoint. A scoring
// error rejects so the progress list surfaces it instead of polling forever.
export function refreshUpload(item: UnifiedItem, bid: string, bearer: httpx.option): CancellablePromise<UnifiedItem> {
	const mid = item.data.media?.id!
	const settle = (next: UnifiedItem): CancellablePromise<UnifiedItem> =>
		next.data.event?.approved === api.Prediction.ERROR ? CancellablePromise.reject(next) : CancellablePromise.resolve(next)
	switch (item.event_type) {
		case "image":
			return api.images.uploads.find(mid, bid, bearer).then((data) => settle({ event_type: "image", data }))
		case "text":
			return api.text.uploads.find(mid, bid, bearer).then((data) => settle({ event_type: "text", data }))
		case "document":
			return api.documents.uploads.find(mid, bid, bearer).then((data) => settle({ event_type: "document", data }))
		case "video":
			return api.videos.uploads.find(mid, bid, bearer).then((data) => settle({ event_type: "video", data }))
	}
}

// resumeUploads lists the profile's uploads still awaiting a score across all
// four asset types so the mixed panel can pick them back up on mount.
export function resumeUploads(
	bid: string,
	pid: string,
	bearer: httpx.option,
): CancellablePromise<inputs.uploads.api.uploaded<UnifiedItem>[]> {
	const pending = {
		offset: 0n,
		limit: 20n,
		uploaded_by: [pid],
		brand_id: bid,
		queues: [uuid.NIL, bid],
		approved: [api.Prediction.NONE],
	}
	const requests = {
		image: api.images.uploads.searches.zero(pending),
		text: api.text.uploads.searchZero(pending),
		document: api.documents.uploads.searches.zero(pending),
		video: api.videos.uploads.searches.zero(pending),
	}
	return fanoutSearch(bid, [], requests, uploadsSearchers, bearer).then((r) =>
		r.items.map((data) => ({
			id: uuid.v4(),
			display: unifiedItemDisplay(data),
			background: layouts.theme.colors.blue.blue,
			data,
		})),
	)
}
